import { useMutation } from '@tanstack/react-query'
import { useHostContext } from '@mobi/account/Common'
import { ErrorWithBody, PrecommitmentUrl, put, queryKeys } from '@mobi/account/Utils'
import { toastIds } from '@mobi/account/Utils/constants'
import { DepositLimitFrequency } from '@mobi/api-types/index'
import { addToast } from '@mobi/component-library/Feedback/Toast'

export type DepositLimitAndAnniversaryRequest = {
  betAccountNumber: number
  optInMarketingIndicator: boolean
  preCommitmentMaxDepositAmount?: number | null
  preCommitmentMaxDepositFrequency?: DepositLimitFrequency | null
  // The CRM API requires these transformations or it will delete any proposed values
  maxBetAmount?: string
  userCreditCardBlockedIndicator?: boolean
  blockPayPalIndicator?: boolean
}

export const useSaveDepositLimitAndAnniversary = () => {
  const { logout } = useHostContext()

  const {
    mutate: saveDepositLimitAndAnniversary,
    status: saveDepositLimitAndAnniversaryStatus,
    reset,
  } = useMutation({
    mutationFn: (preCommitmentDetails: DepositLimitAndAnniversaryRequest): Promise<void | null> =>
      put(
        PrecommitmentUrl.saveDepositLimitAndAnniversary(preCommitmentDetails.betAccountNumber),
        preCommitmentDetails
      ),
    mutationKey: queryKeys.precommitmentDepositLimit,
    onError: (error: ErrorWithBody<{ isAccountClosed?: boolean }>) => {
      if (error.status === 401 && error.body?.isAccountClosed) {
        logout?.().then(() => reset())
        addToast({
          id: toastIds.saveDepositLimitFailure,
          isDismissable: true,
          title: 'We’re unable to save your deposit limit.',
          subtitle: 'Please call 1300 36 36 88 to resolve the matter.',
          icon: 'error',
          timeoutMs: 6_000, // 6s
        })
      }
    },
  })

  return { saveDepositLimitAndAnniversary, saveDepositLimitAndAnniversaryStatus }
}
